/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, April 2020
 */

import React, { useRef, useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Question, FlowBlockQuestion } from '@staffino/model';
import { convertCssUnit } from '@staffino/primitive-controls';
import { TextInputControl } from './controls';
import Header from './header';
import { ScrollAnchor, useScrollEffect } from './scroll';
import isEnterPressed from './input-submit';
import Tooltip from './tooltip';

type Props = {
  block: FlowBlockQuestion;
  question: Question;
};
export default function OpenQuestionBlock({ block, question }: Props) {
  const { t } = useTranslation();
  const context = useFormContext();

  const TextInputControlComponent = context ? Controller : TextInputControl;

  const commentRef = useRef(null);
  const commentError = context?.errors[block.id]?.comment;
  const required = (block as any).required ?? question.required;
  const titleSize = question.extraParams?.titleSize ?? (block as any).titleSize;

  const [enterTriggered, setEnterTriggered] = useState(false);
  const { scrollToError } = useScrollEffect(
    `${block.id}.comment`,
    enterTriggered && !commentError ? 'validValue' : null,
  );

  return (
    <>
      <ScrollAnchor id={`${block.id}.comment`} focusOn={commentRef} />
      <Header
        question={question}
        size={titleSize}
        required={required && !!question.title}
        answerErrorMessage={
          !!commentError &&
          !!question.title &&
          t('Please answer this question.')
        }
      />
      <Tooltip
        tooltip={t('This field is required.')}
        show={required && !question.title}
      >
        <TextInputControlComponent
          blockId={`${block.id}.comment`}
          name={`${block.id}.comment`}
          as={
            <TextInputControl
              ref={(element: any) => {
                context?.register?.(element, { required });
                commentRef.current = element;
              }}
            />
          }
          control={context?.control}
          rules={{ required }}
          type={question.skin || 'SingleLine'}
          invalid={!!commentError}
          requiredValidationMessage={
            !!commentError && !question.title && t('Please fill out this field')
          }
          placeholder={`${question.commentLabel || t('Your answer')}${
            required && !question.title ? ' *' : ''
          }`}
          limit={question.extraParams?.limit ?? (block as any).limit}
          activeColor="primary"
          width={1}
          maxWidth={convertCssUnit('30rem')}
          marginTop={
            question.title || question.description
              ? convertCssUnit(titleSize === 'small' ? '0.75rem' : '2rem')
              : 0
          }
          marginLeft="auto"
          marginRight="auto"
          marginBottom={0}
          onKeyDown={(event) => setEnterTriggered(isEnterPressed(event))}
          onFocus={() => scrollToError(`${block.id}.comment`)}
        />
      </Tooltip>
    </>
  );
}
